import { useState } from 'react';
import type { GoalChoice, GoalKey } from '@st/contract';
import { useGoalChoices, useSubmitGoal } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { Chip, NumberInput, SkeletonCard } from '../../ui';
import { WizardScreen } from './WizardScreen';

export interface GoalStepProps {
  screenIndex: number;
  totalScreens: number;
  onDone: () => void;
  onBack: () => void;
}

export function GoalStep(props: GoalStepProps) {
  const { t } = useTranslation();
  const choices = useGoalChoices();

  return (
    <QueryBoundary
      query={choices}
      skeleton={<SkeletonCard />}
      isEmpty={(d) => d.items.length === 0}
      empty={{ message: t('wizard.noPrograms') }}
    >
      {(data) => <GoalForm {...props} choices={data.items} />}
    </QueryBoundary>
  );
}

/**
 * Drugi korak: cilj. Odabir „Skinuti kilograme” ODMAH otvara polje za željenu
 * kilažu ispod kartica — trener je tražio da se ne ide dalje bez nje, a server
 * isti uvjet vraća kao grešku na polju (`targetWeightKg`).
 */
function GoalForm({
  choices,
  screenIndex,
  totalScreens,
  onDone,
  onBack,
}: GoalStepProps & { choices: GoalChoice[] }) {
  const { t } = useTranslation();
  const submit = useSubmitGoal();
  const [goal, setGoal] = useState<GoalKey | null>(null);
  const [targetWeight, setTargetWeight] = useState<number | ''>('');

  const selected = choices.find((c) => c.key === goal) ?? null;
  const needsWeight = selected?.needsTargetWeight ?? false;
  const weightMissing = needsWeight && typeof targetWeight !== 'number';
  const canAdvance = selected !== null && !weightMissing;

  async function next(): Promise<void> {
    if (!selected || weightMissing) return;
    await submit.mutateAsync({
      goal: selected.key,
      ...(needsWeight ? { targetWeightKg: Number(targetWeight) } : {}),
    });
    onDone();
  }

  return (
    <WizardScreen
      eyebrow={t('wizard.goal')}
      question={t('wizard.goalQuestion')}
      help={t('wizard.goalHelp')}
      index={screenIndex}
      total={totalScreens}
      onNext={() => void next()}
      onBack={onBack}
      nextDisabled={!canAdvance}
      loading={submit.isPending}
      error={submit.isError ? toErrorMessage(t, submit.error).title : undefined}
    >
      <div className="grid gap-3 sm:grid-cols-2" role="group" aria-label={t('wizard.goalQuestion')}>
        {choices.map((choice) => (
          <Chip
            key={choice.key}
            selected={goal === choice.key}
            onClick={() => {
              setGoal(choice.key);
              if (!choice.needsTargetWeight) setTargetWeight('');
            }}
          >
            {choice.label}
          </Chip>
        ))}
      </div>

      {needsWeight && (
        <div className="mt-5 rounded-xl border border-line-subtle bg-bg-alt p-4">
          <NumberInput
            id="wz-targetWeight"
            label={t('wizard.targetWeight')}
            hint={t('wizard.targetWeightHint')}
            suffix="kg"
            step={0.5}
            autoFocus
            value={targetWeight}
            onValueChange={setTargetWeight}
          />
          {weightMissing && (
            <p role="alert" className="mt-2 text-[12px] text-danger">
              {t('wizard.targetWeightRequired')}
            </p>
          )}
        </div>
      )}
    </WizardScreen>
  );
}
